import React, { useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { COLLECTIONS_BY_BRAND, PRODUCTS, IMAGES } from '../mock';

const slugifyBrand = (b) =>
  b.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const BrandModels = () => {
  const { brand: brandSlug } = useParams();

  const brand = useMemo(() => {
    return Object.keys(COLLECTIONS_BY_BRAND).find((b) => slugifyBrand(b) === brandSlug);
  }, [brandSlug]);

  const models = brand ? COLLECTIONS_BY_BRAND[brand] : [];
  const productCounts = useMemo(() => {
    const counts = {};
    PRODUCTS.forEach((p) => {
      counts[p.collectionId] = (counts[p.collectionId] || 0) + 1;
    });
    return counts;
  }, []);

  if (!brand) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="bg-white text-neutral-900 min-h-screen">
      {/* Hero */}
      <section
        className="relative pt-40 pb-16 text-white"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.92), rgba(0,0,0,0.7)), url(${IMAGES.workshop})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="font-display tracking-[0.3em] text-xs text-[#FF6B00] font-semibold mb-3">
            {models.length} MODEL{models.length !== 1 ? 'S' : ''}
          </div>
          <h1 data-testid="brand-title" className="font-display text-5xl md:text-7xl tracking-tight">
            {brand.toUpperCase()}
          </h1>
          <p className="text-neutral-300 mt-4 max-w-2xl">
            Browse every {brand} chassis we currently outfit. Pick a model to see all available carbon, aero and forged parts.
          </p>
        </div>
      </section>

      {/* Breadcrumb */}
      <section className="border-b border-neutral-100 py-4">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-sm text-neutral-500">
          <Link to="/" className="hover:text-[#FF6B00]">Home</Link>
          <span className="mx-2">/</span>
          <span className="text-neutral-900">{brand}</span>
        </div>
      </section>

      {/* Models grid */}
      <section className="py-14">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          {models.length === 0 ? (
            <div className="text-neutral-500 py-20 text-center">
              No models available yet for this brand.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="brand-model-grid">
              {models.map((m) => {
                const count = productCounts[m.id] || 0;
                return (
                  <Link
                    key={m.id}
                    to={`/collections/${m.id}`}
                    data-testid={`model-card-${m.id}`}
                    className="group block"
                  >
                    <div className="aspect-[4/3] bg-neutral-100 overflow-hidden">
                      {m.image ? (
                        <img
                          src={m.image}
                          alt={m.name}
                          loading="lazy"
                          className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-neutral-400 font-display tracking-[0.3em] text-xs">
                          {m.name.toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="py-4 border-b-2 border-transparent group-hover:border-[#FF6B00] transition-colors">
                      {m.years && (
                        <div className="font-display tracking-[0.22em] text-[10px] text-[#FF6B00] font-semibold mb-1">
                          {m.years}
                        </div>
                      )}
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="font-display text-xl tracking-wide group-hover:text-[#FF6B00] transition-colors truncate">
                            {m.name}
                          </h3>
                          <p className="text-neutral-500 text-sm mt-1">
                            {count > 0 ? `${count} part${count !== 1 ? 's' : ''} available` : 'View parts'}
                          </p>
                        </div>
                        <ArrowRight
                          size={18}
                          className="text-neutral-400 group-hover:text-[#FF6B00] group-hover:translate-x-1 transition-all mt-1 flex-shrink-0"
                        />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BrandModels;
